import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';

/**
 * 首页 Landing（2026-08 用户要求）：
 * - 项目介绍（Hero + 功能卡片）
 * - 保留登录/注册入口（滚动到登录卡片）
 * - 不暴露任何默认账号信息
 */
export default function LoginPage() {
  const { login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (isNative) {
      scrollToLogin();
    }
  }, [isNative]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        await register(username, password);
      } else {
        await login(username, password);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || (!err?.response ? '网络连接失败，请检查网络或后端连接' : '操作失败，请重试');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const scrollToLogin = () => {
    const el = document.getElementById('login-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    window.scrollTo({ top: el ? el.offsetTop - 20 : 1000, behavior: 'smooth' });
    setTimeout(() => document.querySelector<HTMLInputElement>('#login-username')?.focus(), 300);
  };

  const features = [
    { icon: '💬', title: '智能问答', desc: 'Agentic RAG 按需检索健身 UP 主知识库，回答动作要领、训练原理、饮食营养等问题，每个回答附参考来源' },
    { icon: '📝', title: '训练记录', desc: '聊天中说"练了卧推 60kg 5组"，自动识别并写入训练档案，无需手动填写' },
    { icon: '📅', title: '训练日历', desc: '月历视图展示每天的训练动作与重量，训练轨迹一目了然' },
    { icon: '📋', title: '训练计划', desc: '一句话生成结构化训练计划，按目标/器械/频率定制，随时查看调整' },
    { icon: '📚', title: '数据源订阅', desc: '订阅你关注的 B站/YouTube 健身 UP 主，还可申请接入新的数据源' },
    { icon: '🔍', title: '知识溯源', desc: '回答可展开参考片段、跳转原视频，拒绝编造，知识有据可查' },
  ];

  return (
    <div className="landing">
      {/* 顶部导航 */}
      <header className="landing-nav">
        <div className="landing-brand">
          <span className="landing-brand-icon">💪</span>
          <span>GymRAG</span>
        </div>
        <button className="landing-nav-login" onClick={scrollToLogin}>
          登录
        </button>
      </header>

      {/* Hero */}
      <section className="landing-hero">
        <h1>你的 AI 健身助手</h1>
        <p className="landing-hero-sub">
          基于健身 UP 主视频知识库的智能问答 · 训练记录 · 训练日历 · 训练计划
        </p>
        <div className="landing-cta">
          <button className="landing-cta-primary" onClick={scrollToLogin}>
            立即开始 →
          </button>
          <a href="#features" className="landing-cta-secondary">
            了解功能 ↓
          </a>
        </div>
        <p className="landing-hero-tag">
          🚀 Agentic RAG · 回答附视频来源 · 拒绝编造
        </p>
      </section>

      {/* 功能特性 */}
      <section id="features" className="landing-features">
        <h2 className="landing-section-title">核心功能</h2>
        <div className="landing-feature-grid">
          {features.map(f => (
            <div key={f.title} className="landing-feature-card">
              <div className="landing-feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 登录 / 注册 */}
      <section id="login-section" className="landing-login-section">
        <div className="login-card">
          <div className="login-header">
            <span className="login-icon">💪</span>
            <h1>欢迎使用 GymRAG</h1>
            <p>{isRegister ? '创建你的账号' : '登录后开始你的训练之旅'}</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <input
                id="login-username"
                type="text"
                placeholder="用户名"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="login-input"
                required
                minLength={2}
                autoFocus
              />
            </div>
            <div className="form-group">
              <input
                type="password"
                placeholder="密码"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="login-input"
                required
                minLength={4}
              />
            </div>

            {error && <div className="login-error">{error}</div>}

            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? '登录中...' : isRegister ? '注册' : '登录'}
            </button>

            <div className="login-hint">
              <div className="login-hint-text">
                <span>💡 测试账号：</span>
                <code>test</code> / <code>test</code>
              </div>
              <button
                type="button"
                className="login-hint-btn"
                onClick={() => {
                  setIsRegister(false);
                  setUsername('test');
                  setPassword('test');
                }}
              >
                一键填入
              </button>
            </div>

            <div className="login-toggle">
              {isRegister ? (
                <span>已有账号？<a href="#" onClick={e => { e.preventDefault(); setIsRegister(false); }}>去登录</a></span>
              ) : (
                <span>没有账号？<a href="#" onClick={e => { e.preventDefault(); setIsRegister(true); }}>去注册</a></span>
              )}
            </div>
          </form>
        </div>
      </section>

      <footer className="landing-footer">
        © 2026 GymRAG · 你的 AI 健身助手
      </footer>
    </div>
  );
}
